import { forwardRef, useEffect, useState } from 'react';
import { DateValue } from 'react-aria';
import { CalendarDate } from '@internationalized/date';

import { DatePicker, type IDatePickerProps } from '../';

export type UnixDatePickerProps = Omit<IDatePickerProps, 'value' | 'onChange' | 'minValue' | 'maxValue'> & {
    // All values represent dates as Unix timestamps in SECONDS (string)
    value?: string;
    onChange?: (value: string) => void;
    minValue?: string;
    maxValue?: string;
};

// [year, month, day]
type DateValues = Readonly<[number, number, number]>;

// Converts Unix timestamp (string) to date values [year, month, day]
const getDateValues = (value: string): [number, number, number] => {
    // Values are unix timestamps in SECONDS; the Date constructor wants milliseconds.
    const date = new Date(Number(value) * 1000);
    return [date.getFullYear(), date.getMonth() + 1, date.getDate()];
};

// Checks if all date components (year, month, day) are set
// Year must be 4-digit long to prevent premature conversion during user input
// For example, typing "202" is not considered a complete year value
const isDateComplete = (dateValues: DateValues) => {
    const [year, month, day] = dateValues;
    return !!(year.toString().length === 4 && month && day);
};

// Converts Unix timestamp (string) to CalendarDate object
const getCalendarDate = (value: string) => {
    const dateValues = getDateValues(value);
    if (isDateComplete(dateValues)) {
        return new CalendarDate(...dateValues);
    }
};

// Converts date values [year, month, day] to a Unix timestamp in SECONDS
const getUnixTimestamp = (year: number, month: number, day: number) => {
    return Math.round(new Date(year, month - 1, day).getTime() / 1000);
};

export const UnixDatePicker = forwardRef<HTMLDivElement, UnixDatePickerProps>(
    ({ value, onChange, minValue, maxValue, ...props }, ref) => {
        const [calendarDate, setCalendarDate] = useState<CalendarDate | null>(null);

        useEffect(() => {
            if (value) {
                const newCalendarDate = getCalendarDate(value);
                if (newCalendarDate) {
                    setCalendarDate(newCalendarDate);
                }
            } else {
                setCalendarDate(null);
            }
        }, [value]);

        const handleDateChange = (date: DateValue | null) => {
            if (date) {
                const dateValues: DateValues = [date.year, date.month, date.day];
                setCalendarDate(new CalendarDate(...dateValues));
                if (isDateComplete(dateValues)) {
                    onChange?.(getUnixTimestamp(...dateValues).toString());
                }
            } else {
                setCalendarDate(null);
            }
        };

        return (
            <DatePicker
                ref={ref}
                value={calendarDate}
                onChange={handleDateChange}
                minValue={minValue ? getCalendarDate(minValue) : undefined}
                maxValue={maxValue ? getCalendarDate(maxValue) : undefined}
                {...props}
            />
        );
    },
);
